import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';
import { Presentation, Plus, Loader2, CheckCircle2, XCircle, Clock, TrendingUp, MessageSquareWarning, BarChart3, Search, BellRing } from 'lucide-react';
import { isMgr, demoBase } from './demoConstants';
import { computeStats } from './demoStats';
import DemoPeriodSelector from './DemoPeriodSelector';
import SearchSelect from './SearchSelect';
import DemoDetailModal from './DemoDetailModal';
import NudgeFeedbackDialog from './NudgeFeedbackDialog';
import { Card } from '../../../components/ui/card';
import { PageHeader, Stat, DemoTable, EmptyState } from './DemoUI';

const EMPTY = { trainer_id: '', counsellor_id: '', course_id: '', category: '', status: '', outcome: '', feedback: '', q: '' };

const FB_FILTERS = [
  { v: 'any_pending', l: 'Any feedback pending' },
  { v: 'trainer_pending', l: 'Trainer not filled' },
  { v: 'counsellor_pending', l: 'Counsellor not filled' },
  { v: 'both_pending', l: 'Neither filled' },
  { v: 'complete', l: 'Both submitted' },
];

export default function DemoQueuePage() {
  const { user } = useAuth();
  const role = user?.role;
  const mgr = isMgr(role);
  const base = demoBase(role);
  const navigate = useNavigate();

  const [period, setPeriod] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState({ trainers: [], counsellors: [], courses: [] });
  const [f, setF] = useState(EMPTY);
  const [openId, setOpenId] = useState(null);
  const [nudge, setNudge] = useState(false);
  const [nudging, setNudging] = useState('');
  const [bannerHidden, setBannerHidden] = useState(() => sessionStorage.getItem('demoReviewBannerHidden') === '1');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/training/demos/masters/all')
      .then((r) => setMasters({ trainers: r.data.trainers || [], counsellors: r.data.counsellors || [], courses: r.data.courses || [] }))
      .catch(() => {});
    api.get('/training/categories')
      .then((r) => setCategories(r.data.buckets || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const params = { date_from: period.from_date, date_to: period.to_date, exclude_finalised: 'true' };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      if (mgr && !f.status) params.include_all = 'true';
      const d = await api.get('/training/demos', { params });
      setRows(d.data.demos || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load demos'); }
    finally { setLoading(false); }
  }, [period, f, mgr]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k] === v ? '' : v }));
  const stats = useMemo(() => computeStats(rows), [rows]);

  const mySide = role === 'trainer' ? 'trainer' : role === 'counsellor' ? 'counsellor' : null;
  const [pendingMine, setPendingMine] = useState(false);
  const isMineMissing = useCallback((d) => d.status === 'approved' && (mySide === 'trainer' ? !d.trainer_feedback : !d.counsellor_feedback), [mySide]);
  const pendingMineCount = useMemo(() => (mySide ? rows.filter(isMineMissing).length : 0), [rows, mySide, isMineMissing]);
  const visible = useMemo(() => (pendingMine && mySide ? rows.filter(isMineMissing) : rows), [rows, pendingMine, mySide, isMineMissing]);

  const oldestMine = useMemo(() => {
    if (!mySide) return 0;
    const days = rows.filter(isMineMissing).map((d) => Math.floor((Date.now() - new Date(`${d.demo_date}T00:00:00`).getTime()) / 86400000));
    return days.length ? Math.max(...days) : 0;
  }, [rows, mySide, isMineMissing]);

  const kpis = [
    { id: 'approved', icon: Presentation, label: 'Approved Demos', value: stats.total_approved, tone: 'violet', onClick: () => toggle('status', 'approved'), active: f.status === 'approved' },
    ...(mySide ? [{ id: 'mine', icon: MessageSquareWarning, label: 'Pending From You', value: pendingMineCount, tone: 'rose', onClick: () => setPendingMine((v) => !v), active: pendingMine }] : []),
    { id: 'adm', icon: CheckCircle2, label: 'Admissions', value: stats.admissions, tone: 'emerald', onClick: () => toggle('outcome', 'admission'), active: f.outcome === 'admission' },
    { id: 'noadm', icon: XCircle, label: 'No Admission', value: stats.no_admissions, tone: 'rose', onClick: () => toggle('outcome', 'no_admission'), active: f.outcome === 'no_admission' },
    { id: 'fu', icon: Clock, label: 'Follow-ups', value: stats.followups, tone: 'sky', onClick: () => toggle('outcome', 'follow_up'), active: f.outcome === 'follow_up' },
    { id: 'conv', icon: TrendingUp, label: 'Conversion', value: `${stats.conversion_pct}%`, tone: 'amber', sub: `${stats.admissions} of ${stats.total_approved} approved` },
    ...(role === 'counsellor' ? [] : [{ id: 'pa', icon: Clock, label: 'Pending Approval', value: stats.pending_approval, tone: 'violet', onClick: () => toggle('status', 'pending_approval'), active: f.status === 'pending_approval' }]),
    { id: 'pf', icon: MessageSquareWarning, label: 'Pending Feedback', value: stats.pending_feedback, tone: 'slate', sub: 'awaiting trainer / counsellor', onClick: () => toggle('feedback', 'any_pending'), active: f.feedback === 'any_pending' },
  ];

  // who exactly is holding up the feedback — only for managers, on the rows in view
  const pendingBy = useMemo(() => {
    if (!mgr) return { trainers: [], counsellors: [] };
    const t = {}; const c = {};
    visible.forEach((d) => {
      if (d.status !== 'approved' || d.void) return;
      if (!d.trainer_feedback) t[d.trainer_name || '—'] = (t[d.trainer_name || '—'] || 0) + 1;
      if (!d.counsellor_feedback) c[d.counsellor_name || '—'] = (c[d.counsellor_name || '—'] || 0) + 1;
    });
    const sort = (o) => Object.entries(o).sort((a, b) => b[1] - a[1]);
    return { trainers: sort(t), counsellors: sort(c) };
  }, [visible, mgr]);

  const nudgeOne = async (side, id, name) => {
    if (!id) { toast.error(`Couldn’t resolve ${name} — use the Nudge button for the full list`); return; }
    setNudging(`${side}:${id}`);
    try {
      const r = await api.post('/training/demos/nudge', { keys: [`${side}:${id}`] });
      if (r.data.sent) toast.success(`Reminder sent to ${name}`);
      else toast.info(`${name} was nudged in the last 12 hours — cooling down`);
    } catch (e) {
      toast.error(e.response?.data?.detail === 'Nothing to nudge'
        ? `Nothing to nudge for ${name} — their pending demos are in the future`
        : (e.response?.data?.detail || 'Nudge failed'));
    } finally { setNudging(''); }
  };

  return (
    <div className="space-y-6" data-testid="demo-queue-page">
      <PageHeader
        icon={Presentation}
        eyebrow="Trainer Demo"
        title="Demo Queue"
        subtitle="Live pre-admission demos — approve, capture feedback, close the loop. Finalised demos move to the report."
        testid="demo-queue-header"
      >
        <Button variant="outline" size="sm" className="h-10 border-slate-200" onClick={() => navigate(`${base}/analytics`)} data-testid="goto-report-btn">
          <BarChart3 className="w-4 h-4 mr-1.5" />Analytics
        </Button>
        {mgr && (
          <Button variant="outline" size="sm" className="h-10 border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => setNudge(true)} data-testid="nudge-btn">
            <BellRing className="w-4 h-4 mr-1.5" />Nudge pending feedback
          </Button>
        )}
        <Button size="sm" onClick={() => navigate(`${base}/new`)} className="h-10 bg-violet-600 hover:bg-violet-700 text-white" data-testid="new-demo-btn">
          <Plus className="w-4 h-4 mr-1.5" />New Demo
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="demo-kpi-rail">
        {kpis.map(({ id, ...rest }) => <Stat key={id} {...rest} testid={`kpi-${id}`} />)}
      </div>

      <Card className="p-4 border space-y-3" data-testid="demo-filter-bar">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <DemoPeriodSelector onChange={setPeriod} defaultKey="this_month" />
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={f.q} onChange={(e) => set('q', e.target.value)} placeholder="Search demo, student, course…" className="h-10 w-60 pl-9 bg-white" data-testid="demo-search" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          {mgr && <SearchSelect value={f.trainer_id} onChange={(v) => set('trainer_id', v)} options={masters.trainers} allowAll allLabel="All trainers" placeholder="All trainers" testid="filter-trainer" className="w-44" />}
          {(mgr || role === 'trainer') && <SearchSelect value={f.counsellor_id} onChange={(v) => set('counsellor_id', v)} options={masters.counsellors} allowAll allLabel="All counsellors" placeholder="All counsellors" testid="filter-counsellor" className="w-44" />}
          <SearchSelect value={f.course_id} onChange={(v) => set('course_id', v)} options={masters.courses} allowAll allLabel="All courses" placeholder="All courses" testid="filter-course" className="w-48" />
          <SearchSelect value={f.category} onChange={(v) => set('category', v)} options={categories.map((c) => ({ id: c, name: c }))} allowAll allLabel="All technologies" placeholder="All technologies" testid="filter-category" className="w-48" />
          <Select value={f.status || 'all'} onValueChange={(v) => set('status', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-10 w-40 bg-white text-[13px]" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              {role !== 'counsellor' && <SelectItem value="pending_approval">Pending Approval</SelectItem>}
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="not_done">Not Conducted</SelectItem>
              <SelectItem value="not_applicable">Not Applicable</SelectItem>
            </SelectContent>
          </Select>
          <Select value={f.feedback || 'all'} onValueChange={(v) => set('feedback', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-10 w-48 bg-white text-[13px]" data-testid="filter-feedback"><SelectValue placeholder="All feedback" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All feedback</SelectItem>
              {FB_FILTERS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="h-9 text-[12px] text-slate-500" onClick={() => { setF(EMPTY); setPendingMine(false); }} data-testid="clear-filters">Clear</Button>
        </div>
      </Card>

      {mgr && f.feedback && f.feedback !== 'complete' && (pendingBy.trainers.length > 0 || pendingBy.counsellors.length > 0) && (
        <Card className="p-4 border border-amber-200 bg-amber-50/70 space-y-2.5" data-testid="pending-by-person">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
            <MessageSquareWarning className="w-3.5 h-3.5" />Who hasn’t filled the feedback
            <span className="font-medium normal-case tracking-normal text-amber-700/80">· tap a name to filter, the bell to remind them</span>
          </p>
          {[['Trainers', pendingBy.trainers, 'trainer_id', masters.trainers, 'trainer'], ['Counsellors', pendingBy.counsellors, 'counsellor_id', masters.counsellors, 'counsellor']].map(([label, list, key, opts, side]) => (
            list.length > 0 && (
              <div key={label} className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-semibold text-slate-500 w-full sm:w-20">{label}</span>
                {list.map(([name, n]) => {
                  const id = (opts || []).find((o) => o.name === name)?.id;
                  const on = f[key] && f[key] === id;
                  return (
                    <span key={name} className={`inline-flex items-center rounded-full border overflow-hidden transition-colors ${on ? 'bg-amber-600 border-amber-600' : 'bg-white border-amber-200'}`}>
                      <button onClick={() => id && set(key, on ? '' : id)}
                        className={`pl-2.5 pr-2 py-1 text-[11px] font-semibold ${on ? 'text-white' : 'text-amber-900 hover:bg-amber-100'}`}
                        data-testid={`pending-person-${name}`}>
                        {name} <span className="opacity-70">· {n}</span>
                      </button>
                      <button onClick={() => nudgeOne(side, id, name)} disabled={nudging === `${side}:${id}`}
                        title={`Remind ${name} about their pending feedback`}
                        className={`px-2 py-1 border-l ${on ? 'border-amber-500 text-white hover:bg-amber-700' : 'border-amber-200 text-amber-700 hover:bg-amber-100'} disabled:opacity-50`}
                        data-testid={`nudge-person-${name}`}>
                        {nudging === `${side}:${id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
                      </button>
                    </span>
                  );
                })}
              </div>
            )
          ))}
        </Card>
      )}

      {mySide && pendingMineCount > 0 && !bannerHidden && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="review-missing-banner">
          <p className="text-[13px] text-rose-900 flex items-start gap-2.5">
            <MessageSquareWarning className="w-4 h-4 mt-0.5 shrink-0 text-rose-600" />
            <span>
              <b>Your review is missing on {pendingMineCount} demo{pendingMineCount > 1 ? 's' : ''}.</b>{' '}
              {mySide === 'counsellor'
                ? 'The trainer has done their part on some of these — the outcome cannot be recorded until you add your feedback.'
                : 'These demos stay open until you submit your feedback.'}
              {oldestMine > 0 ? <span className="block text-[12px] text-rose-700 mt-0.5">Oldest is {oldestMine} day{oldestMine === 1 ? '' : 's'} old.</span> : null}
            </span>
          </p>
          <span className="flex items-center gap-2">
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setPendingMine(true)} data-testid="review-banner-show">Show them</Button>
            <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => { setBannerHidden(true); sessionStorage.setItem('demoReviewBannerHidden', '1'); }} data-testid="review-banner-dismiss">Dismiss</Button>
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading demos…</div>
      ) : visible.length === 0 ? (
        <Card className="border">
          <EmptyState
            title={pendingMine ? 'Nothing pending from your side' : 'No open demos in this period'}
            hint="Finalised demos live in Demo Analytics. Change the period, or schedule a new demo."
            action={<Button size="sm" onClick={() => navigate(`${base}/new`)} className="bg-violet-600 hover:bg-violet-700" data-testid="empty-new-demo">New Demo</Button>}
          />
        </Card>
      ) : (
        <DemoTable rows={visible} onRowClick={(d) => setOpenId(d.id)} showTrainer={role !== 'trainer'} showCounsellor={role !== 'counsellor'} mySide={mySide} />
      )}

      <DemoDetailModal demoId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <NudgeFeedbackDialog open={nudge} onClose={() => setNudge(false)} />
    </div>
  );
}
